/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QuizData, QuizPage, UserProfile, QuizState } from '../types/quiz';
import { evaluateSkipCondition, replaceDynamicFields, calculateBMI } from '../utils/quizLogic';
import PageRenderer from './quiz-pages/PageRenderer';
import ProgressBar from './quiz-pages/ProgressBar';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { SUPPORTED_LANGS, Lang } from '../i18n/detectLanguage';
import trackEvent, { trackPixel } from '../utils/trackEvent';
import { trackVirtualPageview } from '../utils/trackVirtualPageview';
import { CURRENT_MODEL_VERSION, CURRENT_QUIZ_VERSION, CURRENT_SCORING_REVISION } from '../utils/assessmentVersion';
import { computeRecommendedTrack } from '../utils/trackRouting';
import { computeV3Result } from '../utils/scoringV3';
import '../assets/scss/quiz-editorial.scss';

const QUIZ_DRAFT_COOKIE_PREFIX = 'dys_quiz_draft_';
const EMAIL_COOKIE_NAME = 'dys_quiz_email';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

type QuizDraft = {
    profile?: UserProfile;
    currentPageIndex?: number;
};

const safeCookieName = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, '_');

const readCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;

    const prefix = `${name}=`;
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(prefix));

    if (!cookie) return null;

    try {
        return decodeURIComponent(cookie.slice(prefix.length));
    } catch {
        return null;
    }
};

const writeCookie = (name: string, value: string) => {
    if (typeof document === 'undefined') return;

    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};

const readRememberedEmail = (): string | undefined => {
    const cookieEmail = readCookie(EMAIL_COOKIE_NAME)?.trim();

    if (cookieEmail) return cookieEmail;

    try {
        return window.localStorage.getItem('userEmail')?.trim() || undefined;
    } catch {
        return undefined;
    }
};

const compactProfileForCookie = (profile: UserProfile): UserProfile =>
    Object.fromEntries(
        Object.entries(profile).filter(
            ([, value]) =>
                value === null ||
                ['string', 'number', 'boolean'].includes(typeof value) ||
                (Array.isArray(value) && value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))),
        ),
    ) as UserProfile;

const readQuizDraft = (quizId?: string): QuizDraft => {
    const rememberedEmail = readRememberedEmail();

    if (!quizId) return rememberedEmail ? { profile: { email: rememberedEmail } } : {};

    const rawDraft = readCookie(`${QUIZ_DRAFT_COOKIE_PREFIX}${safeCookieName(quizId)}`);

    if (!rawDraft) return rememberedEmail ? { profile: { email: rememberedEmail } } : {};

    try {
        const parsed = JSON.parse(rawDraft) as QuizDraft;
        const profile = {
            ...(parsed.profile || {}),
            ...(rememberedEmail && !parsed.profile?.email ? { email: rememberedEmail } : {}),
        };

        return {
            profile,
            currentPageIndex: Number.isInteger(parsed.currentPageIndex) ? parsed.currentPageIndex : undefined,
        };
    } catch {
        return rememberedEmail ? { profile: { email: rememberedEmail } } : {};
    }
};

const persistQuizDraft = (quizId: string | undefined, profile: UserProfile, currentPageIndex: number) => {
    const email = typeof profile.email === 'string' ? profile.email.trim() : '';

    if (email) {
        writeCookie(EMAIL_COOKIE_NAME, email);
        try {
            window.localStorage.setItem('userEmail', email);
        } catch {
            // Ignore storage restrictions; cookie persistence above is primary.
        }
    }

    if (!quizId) return;

    writeCookie(
        `${QUIZ_DRAFT_COOKIE_PREFIX}${safeCookieName(quizId)}`,
        JSON.stringify({
            profile: compactProfileForCookie(profile),
            currentPageIndex,
            updatedAt: new Date().toISOString(),
        }),
    );
};

// ── Per-step URL slugs ───────────────────────────────────────────────────────
// Each quiz step gets its own URL so GA / GTM / Clarity can chart how far people
// get (e.g. /quiz/doyourstep-quiz/q1, /email, /payment). Milestone pages get a
// semantic slug; every other page uses its stable id. Index 0 (welcome) keeps
// the bare /quiz/:quizId URL. This is client-side routing only — no page reload.
const SLUG_BY_TYPE: Record<string, string> = {
    email_input: 'email',
    sales: 'paywall',
    // The paywall funnel's five screens. `paywall_offer` reuses the `paywall`
    // slug the old sales page had, so the GA funnel keeps its history — the two
    // never appear in the same quiz.
    paywall_analyzing: 'analyzing',
    paywall_trust: 'trust',
    paywall_profile: 'profile',
    paywall_scratch: 'discount',
    paywall_offer: 'paywall',
};

// Screens that paint their own full-bleed chrome. The quiz's top bar and
// progress rail are hidden on these: a progress bar above a paywall would
// promise a step that does not exist.
const FULL_BLEED_TYPES = new Set([
    'paywall_analyzing',
    'paywall_trust',
    'paywall_profile',
    'paywall_scratch',
    'paywall_offer',
]);

// Synthetic bookend pages for the top-up flow (built further down). They reuse
// `info_content`, so PageRenderer needs no new component; the ids live up here
// because the slug map has to know them.
const EXTENDED_INTRO_ID = 'ext_intro';
const EXTENDED_DONE_ID = 'ext_done';

// Readable slug for a specific page, overriding the id-based default.
const SLUG_BY_ID: Record<string, string> = {
    [EXTENDED_DONE_ID]: 'done',
};

const slugForPage = (page: QuizPage): string =>
    SLUG_BY_ID[page.id] || SLUG_BY_TYPE[page.type] || page.id;

// The funnel lives at /quiz/:quizId. Topping up a direction after payment lives
// at /quiz-extended/:quizId/:track — a separate namespace so an existing
// customer's steps never land in the acquisition funnel's GA paths.
const basePathFor = (quizId: string, track?: string): string =>
    track ? `/quiz-extended/${quizId}/${track}` : `/quiz/${quizId}`;

const pathForIndex = (basePath: string, pages: QuizPage[], index: number): string => {
    const page = pages[index];

    // Defensive: an out-of-range index (e.g. a stale draft from an older/longer
    // quiz) must not crash the URL-sync effect on `page.type`.
    if (index <= 0 || !page) return basePath;

    return `${basePath}/${slugForPage(page)}`;
};

const indexForSlug = (slug: string | undefined, pages: QuizPage[]): number | null => {
    if (!slug) return 0;
    const i = pages.findIndex((page) => slugForPage(page) === slug);

    return i >= 0 ? i : null;
};

// ── v3 multi-segment loading ─────────────────────────────────────────────────
// The v3 shell (doyourstep_v3) carries a `track_quiz_slot` marker. Once the user
// picks a direction we lazily fetch ONLY that track's questions and splice them
// in at the marker. Injection is always AFTER the current step (the marker sits
// after track_select), so existing indices stay valid.
const TRACK_SLOT_TYPE = 'track_quiz_slot';

const spliceTrackIntoPages = (pages: QuizPage[], trackPages: QuizPage[]): QuizPage[] => {
    const i = pages.findIndex((p) => p.type === TRACK_SLOT_TYPE);

    if (i < 0) return pages; // already injected (or no slot)

    return [...pages.slice(0, i), ...trackPages, ...pages.slice(i + 1)];
};

// Fetch a local quiz artifact by stem, with the same EN fallback + content-type
// guard the shell loader uses (CRA can serve index.html for a missing JSON).
const fetchLocalQuiz = async (stem: string, lang: string): Promise<QuizData | null> => {
    const tryFetch = async (lng: string): Promise<QuizData | null> => {
        try {
            const res = await fetch(`/quizzes/${stem}.${lng}.json`, { cache: 'no-store' });

            if (!res.ok) return null;
            if (!(res.headers.get('content-type') || '').includes('application/json')) return null;
            const data = (await res.json()) as QuizData;

            return Array.isArray(data.pages) ? data : null;
        } catch {
            return null;
        }
    };

    return (await tryFetch(lang)) ?? (lang === 'en' ? null : tryFetch('en'));
};

// Count answerable "question units" in a set of pages (single_choice + each
// scale_group statement), and how many are answered in the profile. Drives the
// progress bar so a matrix screen counts its 5 statements, not 1.
const countUnits = (pages: QuizPage[], profile: UserProfile): { total: number; answered: number } => {
    let total = 0;
    let answered = 0;
    const isAnswered = (v: any) => v !== undefined && v !== null && v !== '';

    pages.forEach((page) => {
        if (page.type === 'single_choice' && page.data_field) {
            total += 1;
            if (isAnswered(profile[page.data_field])) answered += 1;
        } else if (page.type === 'scale_group' && Array.isArray((page as any).statements)) {
            (page as any).statements.forEach((st: any) => {
                total += 1;
                if (isAnswered(profile[st.data_field])) answered += 1;
            });
        }
    });

    return { total, answered };
};

// Virtual pageview for GA/GTM funnels. Clarity auto-tracks the pushState itself.
// trackVirtualPageview sends the full page_location/page_title so GA4 attributes
// each step to its own URL instead of the entry URL.
const fireStepPageview = (path: string, page: QuizPage | undefined): void => {
    trackVirtualPageview(path, {
        page_slug: page ? slugForPage(page) : undefined,
        step_id: page?.id,
        step_type: page?.type,
    });
};

const extendedIntroPage = (title: string, lang: string): QuizPage =>
    ({
        id: EXTENDED_INTRO_ID,
        type: 'info_content',
        title: lang === 'uk' ? `Напрям «${title}»` : `Direction: ${title}`,
        description:
            lang === 'uk'
                ? 'Ти вже проходив діагностику, тому питання лише по цьому напряму – без повторного вступу. Відповідай так, як є зараз.'
                : 'You have taken the diagnostic already, so these are only this direction’s questions. Answer for how things are now.',
        buttonText: lang === 'uk' ? 'Почати →' : 'Begin →',
    }) as unknown as QuizPage;

const extendedDonePage = (title: string, lang: string): QuizPage =>
    ({
        id: EXTENDED_DONE_ID,
        type: 'info_content',
        title: lang === 'uk' ? 'Дякуємо!' : 'Thank you!',
        description:
            lang === 'uk'
                ? `Напрям «${title}» готовий – він відкрився на дашборді разом зі своїми патернами.`
                : `The ${title} direction is ready – it is now unlocked on your dashboard along with its patterns.`,
        buttonText: lang === 'uk' ? 'До дашборду →' : 'Go to dashboard →',
    }) as unknown as QuizPage;

const QuizController: React.FC = () => {
    const { quizId, step, track } = useParams<{ quizId: string; step?: string; track?: string }>();
    const navigate = useNavigate();
    const { lang, setLang } = useLang();
    const { user } = useAuth();
    // `track` in the URL means the top-up flow: one direction's questions only,
    // no routing block, no direction picker, no email step and no paywall.
    const isExtended = !!track;
    const basePath = basePathFor(quizId || '', track);
    // Separate draft cookie per flow, so a top-up run never overwrites an
    // in-progress funnel run (or another direction's top-up).
    const draftKey = isExtended ? `${quizId}-ext-${track}` : quizId;

    // Funnel + referral attribution: quiz started. Tagged with the flow so a
    // top-up run by an existing customer isn't counted as a new funnel entry.
    useEffect(() => {
        if (quizId) {
            trackEvent('quiz_started', {
                quizId,
                metadata: { flow: isExtended ? 'extended' : 'funnel', ...(track ? { track } : {}) },
            });
        }
    }, [quizId, isExtended, track]);

    const [quizData, setQuizData] = useState<QuizData | null>(null);
    // Fire the "quiz started" pixel once - when the user actually clicks Begin.
    const quizStartedPixelRef = useRef(false);
    const [state, setState] = useState<QuizState>(() => {
        const draft = readQuizDraft(draftKey);

        return {
            currentPageIndex: draft.currentPageIndex || 0,
            userProfile: draft.profile || {},
            isLoading: true,
            error: null,
        };
    });

    // Live ref to the current step index, so the URL<->step sync effects below
    // read the latest value without re-subscribing (avoids feedback loops).
    const currentIndexRef = useRef(state.currentPageIndex);

    currentIndexRef.current = state.currentPageIndex;
    // The last index we've already mirrored to the URL. Guards the sync effect so
    // it fires exactly once per index value — never on unrelated re-renders (e.g.
    // react-router recreating `navigate`), which would otherwise loop.
    const lastSyncedIndexRef = useRef(-1);
    const stepSyncInitedRef = useRef(false);
    // Which track's questions have been spliced into quizData.pages (guards against
    // double-injection). Reset when the quiz reloads.
    const injectedTrackRef = useRef<string | null>(null);
    // Pristine shell pages (with the track_quiz_slot marker intact). The marker is
    // consumed on the first injection, so we splice every chosen track into THIS
    // base rather than the already-injected array - otherwise switching directions
    // finds no slot and silently keeps the previous track's questions.
    const baseShellRef = useRef<QuizPage[] | null>(null);

    // Lazily fetch the chosen track's questions and splice them into the shell at
    // the `track_quiz_slot` marker. Idempotent per track. Declared early so the
    // marker-trigger effect and answer handlers below can reference it.
    const ensureTrackLoaded = useCallback(
        async (trackId: string) => {
            if (!trackId || injectedTrackRef.current === trackId) return;

            const track = await fetchLocalQuiz(`doyourstep_track_${trackId}`, lang);

            if (!track) return;
            injectedTrackRef.current = trackId;
            setQuizData((prev) => {
                if (!prev) return prev;
                // Splice into the pristine shell (marker intact) so a re-pick
                // REPLACES the prior track instead of no-oping on a missing slot.
                const base = baseShellRef.current || prev.pages;

                return { ...prev, pages: spliceTrackIntoPages(base, track.pages) };
            });
        },
        [lang],
    );

    // Load quiz from Supabase (per-lang row, EN fallback, then legacy
    // unfiltered) or local file. The unfiltered fallback covers the window
    // before the funnels.lang migration is applied - the column doesn't exist
    // yet, so a `lang=eq.X` filter 4xx's and we drop the filter.
    useEffect(() => {
        const fetchSupabaseRow = async (
            supabaseUrl: string,
            apiKey: string,
            query: string,
        ): Promise<QuizData | null> => {
            const response = await fetch(`${supabaseUrl}/rest/v1/funnels?${query}`, {
                headers: { 'Content-Type': 'application/json', apiKey },
                cache: 'no-store',
            });

            if (!response.ok) return null;
            const rows = await response.json();

            if (!rows?.length || !rows[0].data) return null;
            const { data } = rows[0];

            return data as QuizData;
        };

        const fetchLocal = async (quizName: string, quizLang: string): Promise<QuizData | null> => {
            const tryFetch = async (lng: string): Promise<QuizData | null> => {
                try {
                    const res = await fetch(`/quizzes/${quizName}.${lng}.json`, { cache: 'no-store' });

                    if (!res.ok) return null;
                    const contentType = res.headers.get('content-type') || '';

                    // CRA's development server may return index.html with 200
                    // for a missing localized JSON file. Treat it as missing so
                    // the EN fallback can run instead of throwing on res.json().
                    if (!contentType.includes('application/json')) return null;
                    const data = (await res.json()) as QuizData;

                    return Array.isArray(data.pages) ? data : null;
                } catch {
                    return null;
                }
            };

            return (await tryFetch(quizLang)) ?? (quizLang === 'en' ? null : tryFetch('en'));
        };

        // Set the loaded quiz and, if the user is resuming a run where a track was
        // already chosen, splice that track's questions in BEFORE we clear the
        // loading flag — so the URL-sync / getCurrentPage effects never run against
        // the short shell array while the resumed index points past it.
        const applyLoadedQuiz = async (data: QuizData, source: string) => {
            let finalData = data;
            const draft = readQuizDraft(draftKey);
            const resumedTrack = draft.profile?.selected_track as string | undefined;

            // Remember the un-spliced shell so track switches always have a slot.
            if (data.pages.some((p) => p.type === TRACK_SLOT_TYPE)) {
                baseShellRef.current = data.pages;
            }

            if (resumedTrack && data.pages.some((p) => p.type === TRACK_SLOT_TYPE)) {
                const track = await fetchLocalQuiz(`doyourstep_track_${resumedTrack}`, lang);

                if (track) {
                    finalData = { ...data, pages: spliceTrackIntoPages(data.pages, track.pages) };
                    injectedTrackRef.current = resumedTrack;
                }
            }

            setQuizData(finalData);
            setState((prev) => {
                // Guard the resumed index against a stale/foreign draft. v2 drafts
                // (or any older, longer quiz) can carry a currentPageIndex past the
                // v3 shell's length, or into the middle of a v3 run that never
                // happened. Restart from 0 unless this draft clearly belongs to an
                // in-progress v3 run (routing answered or a track already chosen).
                let idx = prev.currentPageIndex;
                const isV3Shell = finalData.pages.some((p) => p.type === TRACK_SLOT_TYPE);

                if (isV3Shell) {
                    const hasV3Progress =
                        !!(prev.userProfile.recommended_track || prev.userProfile.selected_track) ||
                        Object.keys(prev.userProfile).some((k) => k.startsWith('q-screen'));

                    if (!hasV3Progress) idx = 0;
                }
                if (idx < 0 || idx >= finalData.pages.length) idx = 0;

                return { ...prev, isLoading: false, currentPageIndex: idx };
            });
            console.log(`📋 Quiz loaded from ${source}:`, finalData.quiz_name);
        };

        const loadQuiz = async () => {
            if (!quizId) return;
            try {
                // Top-up flow: just this direction's questions, wrapped in an
                // intro and a thank-you. No shell, so no routing block, no
                // direction picker, no email step and no paywall.
                if (isExtended && track) {
                    const trackData = await fetchLocalQuiz(`doyourstep_track_${track}`, lang);

                    if (!trackData) throw new Error(`Unknown direction: ${track}`);
                    const title = trackData.metadata?.title || track;

                    await applyLoadedQuiz(
                        {
                            ...trackData,
                            // A single-entry catalog is what makes computeV3Result
                            // treat this as a v3 run and emit `_scores_v3`, and
                            // what gives the progress bar its question count.
                            tracks: {
                                [track]: {
                                    code: track,
                                    label: title,
                                    question_count: trackData.metadata?.question_count,
                                },
                            } as any,
                            pages: [
                                extendedIntroPage(title, lang),
                                ...trackData.pages,
                                extendedDonePage(title, lang),
                            ],
                        },
                        'local track artifact (extended)',
                    );

                    return;
                }

                // The versioned local artifact is the source of truth for the
                // main DoYourStep funnel. This prevents a stale Supabase row
                // from silently serving an older question model.
                if (quizId === 'doyourstep-quiz') {
                    const localV3 = await fetchLocal('doyourstep_v3', lang);

                    if (localV3) {
                        await applyLoadedQuiz(localV3, 'local v3 artifact');

                        return;
                    }
                }

                const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                const apiKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

                if (supabaseUrl && apiKey) {
                    const queries = [
                        `name=eq.${quizId}&lang=eq.${lang}`,
                        ...(lang === 'en' ? [] : [`name=eq.${quizId}&lang=eq.en`]),
                        `name=eq.${quizId}`,
                    ];

                    const tryQueries = async (i: number): Promise<QuizData | null> => {
                        if (i >= queries.length) return null;
                        const data = await fetchSupabaseRow(supabaseUrl, apiKey, queries[i]);

                        return data ?? tryQueries(i + 1);
                    };
                    const data = await tryQueries(0);

                    if (data) {
                        await applyLoadedQuiz(data, 'Supabase');

                        return;
                    }
                }

                const localQuizName = quizId === 'doyourstep-quiz' ? 'doyourstep_v3' : quizId;
                const localData = await fetchLocal(localQuizName, lang);

                if (!localData) throw new Error('Quiz not found');
                await applyLoadedQuiz(localData, 'local file');
            } catch (error) {
                console.error('Error loading quiz:', error);
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: lang === 'uk' ? 'Не вдалося завантажити діагностику' : 'Failed to load quiz',
                }));
            }
        };

        loadQuiz();
    }, [quizId, lang]);

    // Mirror the current step into the URL (client-side push — no reload) so each
    // step is its own page, and fire a virtual pageview. Guarded to run once per
    // index value: unrelated re-renders (react-router recreating `navigate`, etc.)
    // are ignored, and the back/forward effect below pre-marks this ref so the two
    // never fight (which previously caused an update-depth loop).
    useEffect(() => {
        if (!quizData || !quizId) return;
        if (lastSyncedIndexRef.current === state.currentPageIndex) return;
        lastSyncedIndexRef.current = state.currentPageIndex;

        const desired = pathForIndex(basePath, quizData.pages, state.currentPageIndex);

        if (window.location.pathname !== desired) {
            navigate(desired);
        }
        fireStepPageview(desired, quizData.pages[state.currentPageIndex]);
    }, [state.currentPageIndex, quizData, quizId]);

    // The direction comes from the URL in the top-up flow, not from a picker
    // screen — seed it so scoring and persistence tag the run correctly.
    useEffect(() => {
        if (!isExtended || !track) return;
        setState((prev) =>
            prev.userProfile.selected_track === track
                ? prev
                : { ...prev, userProfile: { ...prev.userProfile, selected_track: track } },
        );
    }, [isExtended, track]);

    // Browser back/forward: the URL slug changed, so move the step to match it.
    // Skips the initial mount — the state initializer (cookie draft) is the source
    // of truth for where the user resumes; the effect above then fixes the URL.
    useEffect(() => {
        if (!quizData) return;
        if (!stepSyncInitedRef.current) {
            stepSyncInitedRef.current = true;

            return;
        }

        const idx = indexForSlug(step, quizData.pages);

        if (idx != null && idx !== currentIndexRef.current) {
            // Pre-mark as synced so the mirror effect above doesn't push the URL
            // back (the URL is already correct — the user navigated it).
            lastSyncedIndexRef.current = idx;
            setState((prev) => ({ ...prev, currentPageIndex: idx }));
        }
    }, [step, quizData]);

    // Safety net: if we ever land on the (still-empty) track slot — e.g. a
    // deep-linked resume before the forward-flow load kicked in — fetch the
    // chosen track's questions so the marker gets replaced.
    useEffect(() => {
        if (!quizData) return;
        const cur = quizData.pages[state.currentPageIndex];

        if (cur && cur.type === TRACK_SLOT_TYPE) {
            const t =
                (state.userProfile.selected_track as string) || (state.userProfile.recommended_track as string) || '';

            if (t) ensureTrackLoaded(t);
        }
    }, [state.currentPageIndex, quizData, state.userProfile, ensureTrackLoaded]);

    const saveResultsToSupabase = useCallback(
        async (profile: UserProfile) => {
            // The top-up flow never asks for an email — the user is signed in,
            // so take it from the session instead.
            const email = (typeof profile.email === 'string' && profile.email) || user?.email;

            if (!email) return;

            // For the v3 quiz (shell carries a `tracks` catalog) compute the
            // dims/patterns block from the flat answers at submit time — nested
            // blocks don't survive cookie compaction, so recompute here. The
            // persistence mechanism (results.answers jsonb) is otherwise unchanged.
            const v3 = quizData?.tracks ? computeV3Result(profile, quizData) : null;

            try {
                const res = await fetch('/api/submit-quiz', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email,
                        answers: {
                            ...profile,
                            // Lets analytics separate acquisition runs from
                            // existing customers topping up a direction.
                            _flow: isExtended ? 'extended' : 'funnel',
                            _quiz_version: quizData?.version || CURRENT_QUIZ_VERSION,
                            _model_version: quizData?.metadata?.model_version || CURRENT_MODEL_VERSION,
                            _scoring_revision: quizData?.metadata?.scoring_revision || CURRENT_SCORING_REVISION,
                            ...(v3
                                ? {
                                      _scores_v3: {
                                          dims: v3.dims,
                                          patterns: v3.patterns,
                                          top_patterns: v3.top_patterns,
                                      },
                                      _track: v3.selected_track || v3.recommended_track,
                                      _recommended_track: v3.recommended_track,
                                      _selected_patterns: v3.selected_patterns,
                                  }
                                : {}),
                        },
                        quest_name: quizId,
                    }),
                });
                const result = await res.json();

                console.log('💾 Answers saved:', result);
            } catch (err) {
                console.error('Failed to save answers:', err);
            }
        },
        [quizId, quizData, isExtended, user?.email],
    );

    // Get current page with skip logic
    const getCurrentPage = useCallback((): QuizPage | null => {
        if (!quizData) return null;

        let index = state.currentPageIndex;
        const { pages } = quizData;

        while (index < pages.length) {
            const page = pages[index];

            if (page.skip_if && evaluateSkipCondition(page.skip_if, state.userProfile)) {
                console.log(`⏭️ Skipping page ${page.id} due to condition`);
                index += 1;
            } else {
                break;
            }
        }

        if (index !== state.currentPageIndex) {
            setState((prev) => ({ ...prev, currentPageIndex: index }));
        }

        return index < pages.length ? pages[index] : null;
    }, [quizData, state.currentPageIndex, state.userProfile]);

    // Navigate to next page
    const handleNext = useCallback(() => {
        if (!quizData) return;

        // Meta Pixel: "quiz started" fires when the user clicks Begin (leaves the
        // welcome screen) - a real start, not just landing on the page.
        const leavingPage = quizData.pages[state.currentPageIndex];

        if (
            !quizStartedPixelRef.current &&
            leavingPage &&
            (leavingPage.id === 'welcome' || (state.currentPageIndex === 0 && leavingPage.type === 'info_content'))
        ) {
            quizStartedPixelRef.current = true;
            // Standard ViewContent (so a "View content" ad conversion can
            // optimize) + a descriptive custom event, both on the real start.
            trackPixel('track', 'ViewContent', {
                content_name: quizId,
                content_category: 'quiz',
            });
            trackPixel('trackCustom', 'QuizStarted', { quiz_id: quizId });
        }

        const nextIndex = state.currentPageIndex + 1;
        const currentId = quizData.pages[state.currentPageIndex]?.id;

        // Top-up flow ends on its own thank-you page, not the funnel's.
        if (isExtended && currentId === EXTENDED_DONE_ID) {
            navigate('/dashboard');

            return;
        }
        if (nextIndex >= quizData.pages.length) {
            console.log('✅ Quiz completed!');
            persistQuizDraft(draftKey, state.userProfile, state.currentPageIndex);
            navigate(isExtended ? '/dashboard' : `/thank-page/${quizId}`);
        } else {
            setState((prev) => {
                persistQuizDraft(draftKey, prev.userProfile, nextIndex);

                return {
                    ...prev,
                    currentPageIndex: nextIndex,
                };
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [quizData, state.currentPageIndex, state.userProfile, navigate, quizId, isExtended, draftKey]);

    /**
     * Submit the top-up run once we land on its thank-you page.
     *
     * This has to be an effect rather than part of `handleNext`: a scale_group
     * commits its answers with setState and then calls a `handleNext` captured
     * from an earlier render, so that callback still sees the profile from
     * BEFORE the final screen. By the time this effect runs, the render that
     * moved us here already includes those answers. The ref makes it fire once.
     */
    const extendedSubmittedRef = useRef(false);

    useEffect(() => {
        if (!isExtended || !quizData) return;

        // Stepping back off the thank-you page arms it again, so editing an
        // answer and coming forward re-submits. That writes a second row, and
        // the dashboard keeps the newest pass per direction — which is what an
        // edited answer should mean.
        if (quizData.pages[state.currentPageIndex]?.id !== EXTENDED_DONE_ID) {
            extendedSubmittedRef.current = false;

            return;
        }
        if (extendedSubmittedRef.current) return;
        extendedSubmittedRef.current = true;
        saveResultsToSupabase(state.userProfile);
        // Drop the draft so re-entering this direction starts a fresh retake
        // rather than resuming on the thank-you page of a finished run.
        persistQuizDraft(draftKey, {}, 0);
    }, [isExtended, quizData, state.currentPageIndex, state.userProfile, saveResultsToSupabase, draftKey]);

    // Handle page answer
    const handleAnswer = useCallback(
        (value: any, extra?: Record<string, any>) => {
            const currentPage = getCurrentPage();

            if (!currentPage) return;

            console.log(`📝 Answer for ${currentPage.id}:`, value);

            // Picking a direction (its data_field is `selected_track`) kicks off the
            // lazy load of ONLY that track's questions.
            const trackToLoad = currentPage.type === 'track_select' ? String(value || '') : '';

            setState((prev) => {
                // `extra` lets a screen commit fields beside its own answer —
                // the email step's marketing consent, for one — in the same
                // update, so they reach `saveResultsToSupabase` together.
                const newProfile = { ...prev.userProfile, ...(extra || {}) };

                if (currentPage.data_field) {
                    newProfile[currentPage.data_field] = value;
                }
                if (newProfile.weight_current && newProfile.height) {
                    newProfile.bmi = calculateBMI(newProfile.weight_current, newProfile.height);
                }
                persistQuizDraft(
                    draftKey,
                    newProfile,
                    quizData ? Math.min(prev.currentPageIndex + 1, quizData.pages.length - 1) : prev.currentPageIndex,
                );
                if (currentPage.type === 'email_input') {
                    saveResultsToSupabase(newProfile);
                }

                return { ...prev, userProfile: newProfile };
            });

            if (trackToLoad) ensureTrackLoaded(trackToLoad);

            setTimeout(() => {
                handleNext();
            }, 300);
        },
        [getCurrentPage, saveResultsToSupabase, ensureTrackLoaded, handleNext, quizId, quizData],
    );

    // Batch answer for a scale_group screen (5 statements at once). Merges every
    // statement's answer, computes the recommended track when the routing block
    // completes, persists once, and advances a single step.
    const handleAnswerBatch = useCallback(
        (fields: Record<string, any>) => {
            const currentPage = getCurrentPage();

            if (!currentPage) return;

            setState((prev) => {
                const newProfile = { ...prev.userProfile, ...fields };

                if (currentPage.type === 'scale_group' && (currentPage as any).routing && quizData) {
                    const trackCodes = quizData.tracks ? Object.keys(quizData.tracks) : undefined;
                    const { recommendedTrack } = computeRecommendedTrack(newProfile, quizData.pages, trackCodes);

                    if (recommendedTrack) newProfile.recommended_track = recommendedTrack;
                }

                persistQuizDraft(
                    draftKey,
                    newProfile,
                    quizData ? Math.min(prev.currentPageIndex + 1, quizData.pages.length - 1) : prev.currentPageIndex,
                );

                return { ...prev, userProfile: newProfile };
            });

            setTimeout(() => {
                handleNext();
            }, 300);
        },
        [getCurrentPage, handleNext, quizId, quizData],
    );

    const handleBack = useCallback(() => {
        if (state.currentPageIndex > 0) {
            setState((prev) => {
                const previousIndex = prev.currentPageIndex - 1;

                persistQuizDraft(draftKey, prev.userProfile, previousIndex);

                return {
                    ...prev,
                    currentPageIndex: previousIndex,
                };
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [state.currentPageIndex, quizId]);

    // ===== Render guards =====

    if (state.isLoading) {
        return (
            <div className="quiz-controller quiz-controller--loading">
                <div className="quiz-loading">
                    <div className="quiz-loading__spinner" />
                    <p>{lang === 'uk' ? 'Завантажуємо діагностику…' : 'Loading quiz...'}</p>
                </div>
            </div>
        );
    }

    if (state.error || !quizData) {
        return (
            <div className="quiz-controller quiz-controller--error">
                <div className="quiz-error">
                    <h2>{lang === 'uk' ? 'Щось пішло не так' : 'Oops'}</h2>
                    <p>{state.error || (lang === 'uk' ? 'Діагностику не знайдено' : 'Quiz not found')}</p>
                    <button type="button" onClick={() => navigate('/')}>
                        {lang === 'uk' ? 'На головну →' : 'Go home →'}
                    </button>
                </div>
            </div>
        );
    }

    const currentPage = getCurrentPage();

    if (!currentPage) return null;

    // Landed on the track slot before its questions finished loading — show the
    // loader (the marker-trigger effect fetches them; the splice then replaces it).
    if (currentPage.type === TRACK_SLOT_TYPE) {
        return (
            <div className="quiz-controller quiz-controller--loading">
                <div className="quiz-loading">
                    <div className="quiz-loading__spinner" />
                    <p>{lang === 'uk' ? 'Готуємо твій напрям…' : 'Preparing your track…'}</p>
                </div>
            </div>
        );
    }

    const processedPage = replaceDynamicFields(currentPage, state.userProfile);
    const totalPages = quizData.pages.length;
    const pageNumber = state.currentPageIndex + 1;

    // Progress. v3: count question units (single_choice + each scale_group
    // statement) and include the chosen track's expected size in the denominator
    // (from the shell catalog) so the bar doesn't jump when the track injects.
    // v2 and other funnels keep the original single_choice-based counter.
    const isV3Quiz = !!quizData.tracks;
    let totalQuestions: number;
    let progressCounter: number;
    let isQuestion: boolean;

    if (isV3Quiz) {
        const NOMINAL_TRACK = 13;
        const routingUnits = quizData.pages.reduce((n, p) => {
            if (p.type === 'single_choice' && (p as any).routing) return n + 1;
            if (p.type === 'scale_group' && (p as any).routing) return n + ((p as any).statements?.length || 0);

            return n;
        }, 0);
        const selTrack =
            (state.userProfile.selected_track as string) || (state.userProfile.recommended_track as string) || '';
        const trackCount =
            quizData.tracks && selTrack && quizData.tracks[selTrack]
                ? quizData.tracks[selTrack].question_count
                : NOMINAL_TRACK;

        totalQuestions = routingUnits + trackCount;
        progressCounter = Math.min(countUnits(quizData.pages, state.userProfile).answered, totalQuestions);
        isQuestion = currentPage.type === 'single_choice' || currentPage.type === 'scale_group';
    } else {
        const questionPages = quizData.pages.filter((p) => p.type === 'single_choice');

        totalQuestions = questionPages.length;
        isQuestion = currentPage.type === 'single_choice';
        progressCounter = isQuestion
            ? questionPages.findIndex((p) => p.id === currentPage.id) + 1
            : quizData.pages.slice(0, state.currentPageIndex).filter((p) => p.type === 'single_choice').length;
    }

    // Endowed progress: never show an empty bar on the opening screens.
    const progressPct = totalQuestions ? 6 + (progressCounter / totalQuestions) * 94 : 0;
    const questionCountText =
        lang === 'uk'
            ? `Питання ${progressCounter} / ${totalQuestions}`
            : `Question ${progressCounter} / ${totalQuestions}`;
    const questionLabel = isQuestion ? questionCountText : undefined;

    if (FULL_BLEED_TYPES.has(currentPage.type)) {
        return (
            <div className="quiz-controller quiz-controller--full">
                <PageRenderer
                    page={processedPage}
                    userProfile={state.userProfile}
                    onAnswer={handleAnswer}
                    onAnswerBatch={handleAnswerBatch}
                    onNext={handleNext}
                    onBack={state.currentPageIndex > 0 ? handleBack : undefined}
                    quizId={quizId}
                    pages={quizData.pages}
                    tracks={quizData.tracks}
                />
            </div>
        );
    }

    return (
        <div className="quiz-controller">
            <header className="quiz-controller__topbar">
                {state.currentPageIndex > 0 ? (
                    <button
                        type="button"
                        className="quiz-controller__back"
                        onClick={handleBack}
                        aria-label={lang === 'uk' ? 'Назад' : 'Go back'}
                    >
                        ← BACK
                    </button>
                ) : (
                    <Link to="/" className="quiz-controller__brand" aria-label="DoYourStep home">
                        <img
                            src={`${process.env.PUBLIC_URL || ''}/images/logo-mark.png`}
                            alt="DoYourStep"
                            style={{ height: 24, width: 'auto', display: 'block' }}
                        />
                    </Link>
                )}
                <div className="quiz-controller__topbar-right">
                    <div
                        className="quiz-controller__lang"
                        role="group"
                        aria-label={lang === 'uk' ? 'Мова' : 'Language'}
                    >
                        {SUPPORTED_LANGS.map((code) => (
                            <button
                                key={code}
                                type="button"
                                className={`quiz-controller__lang-btn ${code === lang ? 'is-active' : ''}`}
                                onClick={() => code !== lang && setLang(code as Lang, { reload: true })}
                                disabled={code === lang}
                                aria-pressed={code === lang}
                            >
                                {code.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <div className="quiz-controller__count">
                        {String(progressCounter).padStart(2, '0')} / {String(totalQuestions).padStart(2, '0')}
                    </div>
                </div>
            </header>

            <ProgressBar progress={progressPct} label={questionLabel} />

            <main className="quiz-controller__content">
                <PageRenderer
                    page={processedPage}
                    userProfile={state.userProfile}
                    onAnswer={handleAnswer}
                    onAnswerBatch={handleAnswerBatch}
                    onNext={handleNext}
                    onBack={state.currentPageIndex > 0 ? handleBack : undefined}
                    quizId={quizId}
                    pages={quizData.pages}
                    tracks={quizData.tracks}
                />
            </main>

            {process.env.NODE_ENV === 'development' && (
                <div className="quiz-debug">
                    <details>
                        <summary>Debug</summary>
                        <pre>
                            Page {pageNumber}/{totalPages} · {currentPage.type}
                            {'\n'}
                            {JSON.stringify(state.userProfile, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </div>
    );
};

export default QuizController;
